/**
 * TaskDetailModal — детали задачи + комментарии + change status / edit / delete.
 */
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Icon } from "@/components/prototype/Icon";
import {
  patchTask, deleteTask, fetchSubtasks, fetchTaskComments, createTaskComment, deleteTaskComment,
  type Task, type TaskStatus,
} from "@/api/tasks";
import { userAvatarUrl } from "@/api/dashboard";
import { useAuth } from "@/auth/AuthContext";
import { parseBackendIso } from "@/lib/presence";

interface Props {
  task: Task;
  onClose: () => void;
  onChanged?: () => void;   // вызывается после patch/delete/comment чтобы родитель refetch'ил
  onEditFull?: (task: Task) => void;  // открыть полноценный wizard для редактирования (опционально)
}

const STATUS_LABELS: Record<TaskStatus, string> = {
  open: "Открыта",
  in_progress: "В работе",
  done: "Завершена",
  cancelled: "Отменена",
};

const STATUS_COLORS: Record<TaskStatus, string> = {
  open: "#0b8043",        // Google green
  in_progress: "#1a73e8", // Google blue
  done: "#9aa0a6",        // Google grey
  cancelled: "#d93025",   // Google red
};

const STATUS_BG_SOFT: Record<TaskStatus, string> = {
  open: "#e6f4ea",
  in_progress: "#e8f0fe",
  done: "#f1f3f4",
  cancelled: "#fce8e6",
};

const STATUS_ICON: Record<TaskStatus, string> = {
  open: "folder",
  in_progress: "clock",
  done: "check",
  cancelled: "x",
};

function fmtDateTime(iso: string | null): string {
  if (!iso) return "";
  const d = parseBackendIso(iso);
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("ru-RU", { day: "2-digit", month: "short", weekday: "short", hour: "2-digit", minute: "2-digit" });
}

export function TaskDetailModal({ task, onClose, onChanged, onEditFull }: Props) {
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();
  const myId = currentUser?.id;

  const subQ = useQuery({
    queryKey: ["tasks/subtasks", task.id],
    queryFn: () => fetchSubtasks(task.id),
    staleTime: 15_000,
  });

  const commentsQ = useQuery({
    queryKey: ["tasks/comments", task.id],
    queryFn: () => fetchTaskComments(task.id),
    staleTime: 5_000,
  });

  const [newComment, setNewComment] = useState("");

  const statusMut = useMutation({
    mutationFn: (st: TaskStatus) => patchTask(task.id, { status: st }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tasks/list", task.topic_id] }); onChanged?.(); },
  });

  const commentMut = useMutation({
    mutationFn: () => createTaskComment(task.id, newComment.trim()),
    onSuccess: () => { setNewComment(""); qc.invalidateQueries({ queryKey: ["tasks/comments", task.id] }); },
    onError: (e: Error) => alert(`Ошибка: ${e.message}`),
  });

  const deleteCommentMut = useMutation({
    mutationFn: (cid: number) => deleteTaskComment(cid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks/comments", task.id] }),
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteTask(task.id),
    onSuccess: () => { onChanged?.(); onClose(); },
    onError: (e: Error) => alert(`Ошибка: ${e.message}`),
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const canDelete = task.creator_user_id === myId || currentUser?.is_superuser;

  const accent = STATUS_COLORS[task.status];

  return (
    <div className="task-modal-bg" onClick={onClose}>
      <div className="task-detail-v2" onClick={(e) => e.stopPropagation()}>
        {/* Header: цветная полоса + закрытие */}
        <div className="task-detail-v2-stripe" style={{ background: accent }}/>
        <button type="button" className="task-detail-v2-close" onClick={onClose} aria-label="Закрыть">
          <Icon name="x" size={20}/>
        </button>

        <div className="task-detail-v2-head">
          <span
            className="task-detail-v2-status-pill"
            style={{ background: STATUS_BG_SOFT[task.status], color: accent }}
          >
            <span className="task-detail-v2-status-dot" style={{ background: accent }}/>
            {STATUS_LABELS[task.status]}
          </span>
          <h2 className="task-detail-v2-title">{task.title}</h2>
          <div className="task-detail-v2-meta">
            {task.deadline_at && (
              <span className="task-detail-v2-meta-chip">
                <Icon name="clock" size={13}/>
                {fmtDateTime(task.deadline_at)}
              </span>
            )}
            <span className="task-detail-v2-meta-chip">
              <Icon name="user" size={13}/>
              Поставил: <b style={{ fontWeight: 500 }}>{task.creator_display_name ?? "—"}</b>
            </span>
          </div>
        </div>

        <div className="task-detail-v2-body">
          {task.description && (
            <section className="task-detail-v2-section">
              <div className="task-detail-v2-section-label">Описание</div>
              <div className="task-detail-v2-desc">{task.description}</div>
            </section>
          )}

          <section className="task-detail-v2-section">
            <div className="task-detail-v2-section-label">
              Исполнители <span className="task-detail-v2-section-count">{task.assignees.length}</span>
            </div>
            <div className="task-detail-v2-assignees">
              {task.assignees.length === 0 && (
                <span className="task-detail-v2-empty">Никто не назначен</span>
              )}
              {task.assignees.map(a => {
                const avaUrl = userAvatarUrl(a.user_id, a.avatar_filename ?? null);
                return (
                  <span key={a.user_id} className="task-detail-v2-assignee">
                    {avaUrl ? (
                      <img src={avaUrl} alt="" className="task-detail-v2-assignee-ava" loading="lazy"/>
                    ) : (
                      <span className="task-detail-v2-assignee-ava task-detail-v2-assignee-ava-fb">
                        {(a.display_name ?? "?")[0]}
                      </span>
                    )}
                    <span>{a.display_name ?? "—"}</span>
                  </span>
                );
              })}
            </div>
          </section>

          <section className="task-detail-v2-section">
            <div className="task-detail-v2-section-label">Статус</div>
            <div className="task-detail-v2-status-row">
              {(["open", "in_progress", "done", "cancelled"] as TaskStatus[]).map(st => {
                const active = task.status === st;
                const color = STATUS_COLORS[st];
                return (
                  <button
                    key={st}
                    type="button"
                    className={`task-detail-v2-status-btn${active ? " is-active" : ""}`}
                    onClick={() => statusMut.mutate(st)}
                    disabled={statusMut.isPending}
                    style={active ? {
                      background: color,
                      borderColor: color,
                      color: "#fff",
                    } : {
                      color: color,
                      borderColor: "#dadce0",
                    }}
                  >
                    <Icon name={STATUS_ICON[st]} size={14}/>
                    {STATUS_LABELS[st]}
                  </button>
                );
              })}
            </div>
          </section>

          {(subQ.data ?? []).length > 0 && (
            <section className="task-detail-v2-section">
              <div className="task-detail-v2-section-label">
                Подзадачи
                <span className="task-detail-v2-section-count">
                  {subQ.data!.filter(s => s.status === "done").length}/{subQ.data!.length}
                </span>
              </div>
              <div className="task-detail-v2-subs">
                {(subQ.data ?? []).map(s => (
                  <div key={s.id} className={`task-detail-v2-sub${s.status === "done" ? " is-done" : ""}`}>
                    <span className="task-detail-v2-sub-tick">
                      {s.status === "done" ? <Icon name="check" size={12}/> : null}
                    </span>
                    <span className="task-detail-v2-sub-title">{s.title}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="task-detail-v2-section">
            <div className="task-detail-v2-section-label">
              Комментарии
              {commentsQ.data && <span className="task-detail-v2-section-count">{commentsQ.data.length}</span>}
            </div>
            <div className="task-detail-v2-comments">
              {commentsQ.isLoading && <div className="task-detail-v2-empty">Загрузка…</div>}
              {commentsQ.data && commentsQ.data.length === 0 && (
                <div className="task-detail-v2-empty">Пока никто не комментировал</div>
              )}
              {(commentsQ.data ?? []).map(c => {
                const avaUrl = userAvatarUrl(c.author_user_id, c.author_avatar_filename);
                const canDel = c.author_user_id === myId || currentUser?.is_superuser;
                return (
                  <div key={c.id} className="task-detail-v2-comment">
                    {avaUrl ? (
                      <img src={avaUrl} alt="" className="task-detail-v2-comment-ava" loading="lazy"/>
                    ) : (
                      <span className="task-detail-v2-comment-ava task-detail-v2-comment-ava-fb">
                        {(c.author_display_name ?? "?")[0]}
                      </span>
                    )}
                    <div className="task-detail-v2-comment-body">
                      <div className="task-detail-v2-comment-head">
                        <span className="task-detail-v2-comment-author">{c.author_display_name ?? "—"}</span>
                        <span className="task-detail-v2-comment-time">
                          {new Date(c.created_at).toLocaleString("ru-RU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </span>
                        {canDel && (
                          <button
                            type="button"
                            className="task-detail-v2-comment-del"
                            onClick={() => {
                              if (confirm("Удалить комментарий?")) deleteCommentMut.mutate(c.id);
                            }}
                            aria-label="Удалить"
                          >
                            <Icon name="x" size={12}/>
                          </button>
                        )}
                      </div>
                      <div className="task-detail-v2-comment-text">{c.text}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="task-detail-v2-comment-add">
              <input
                type="text"
                placeholder="Написать комментарий…"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newComment.trim()) commentMut.mutate();
                }}
              />
              <button
                type="button"
                className="task-detail-v2-comment-send"
                disabled={!newComment.trim() || commentMut.isPending}
                onClick={() => commentMut.mutate()}
              >
                <Icon name="send" size={14}/>
              </button>
            </div>
          </section>
        </div>

        <div className="task-detail-v2-foot">
          {canDelete && (
            <button
              type="button"
              className="task-detail-v2-btn task-detail-v2-btn-danger"
              onClick={() => { if (confirm("Удалить задачу?")) deleteMut.mutate(); }}
            >
              <Icon name="trash" size={14}/> Удалить
            </button>
          )}
          <div style={{ flex: 1 }}/>
          {onEditFull && (
            <button type="button" className="task-detail-v2-btn task-detail-v2-btn-ghost" onClick={() => onEditFull(task)}>
              <Icon name="edit" size={14}/> Редактировать
            </button>
          )}
          <button type="button" className="task-detail-v2-btn task-detail-v2-btn-primary" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}
